import { Component, contentChildren, input, signal } from '@angular/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectOption } from './select-option';
import { NgTemplateOutlet } from '@angular/common';
import { ScribeSuggestionIndicatorComponent } from '../shared/scribe-suggestion-indicator/scribe-suggestion-indicator.component';
import { AmendmentFieldIndicatorComponent } from '../shared/amendment-field-indicator/amendment-field-indicator.component';
import { ScribeSuggestionMarkerTarget } from '../../scribe-suggestion-marker.model';

@Component({
  selector: 'fb-select',
  imports: [
    FormsModule,
    NgSelectModule,
    NgTemplateOutlet,
    ScribeSuggestionIndicatorComponent,
    AmendmentFieldIndicatorComponent,
  ],
  templateUrl: './select.html',
  styleUrl: './select.scss',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: Select, multi: true }],
})
export class Select<SelectionValue> implements ControlValueAccessor {
  /**
   * A text that describes the purpose of the select.
   */
  public readonly label = input.required();

  /**
   * A text that provides users with guidance on what to type into the select.
   */
  public readonly placeholder = input('');

  /**
   * A function to compare the option values with the selected values.
   */
  public readonly compareWith = input((a: SelectionValue, b: SelectionValue) => a === b);
  public readonly markerTarget = input<ScribeSuggestionMarkerTarget | null>(null);
  /**
   * Index of the treatment row this field belongs to, for the amendment indicator.
   */
  public readonly rowIndex = input<number | null>(null);
  public readonly markerFieldKey = input<string>();

  /**
   * Set of options.
   */
  public readonly options = contentChildren(SelectOption);

  /**
   * Value of the select.
   */
  protected readonly value = signal<SelectionValue | null>(null);

  /**
   * Called when the control is blurred.
   */
  protected onTouched: () => void;

  /**
   * Called when the control state changes.
   */
  protected onChange: (value: SelectionValue | null) => void;

  /**
   * Implemented as part of {@link ControlValueAccessor}.
   */
  public writeValue(value: SelectionValue | null): void {
    this.value.set(value);
  }

  /**
   * Implemented as part of {@link ControlValueAccessor}.
   */
  public registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  /**
   * Implemented as part of {@link ControlValueAccessor}.
   */
  public registerOnChange(onChange: (value: SelectionValue | null) => void): void {
    this.onChange = onChange;
  }

  /**
   * Handles changes to the select.
   */
  protected handleSelectChange(value: SelectionValue | null): void {
    // Update local value.
    this.value.set(value);

    // Propagate change to form control.
    this.onChange(value);
  }
}
